import { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { X, Copy, Download, Save, ExternalLink, ImageIcon, Trash2, Users } from 'lucide-react';
import { FotoBottiglieSection } from './FotoBottiglieSection';

const API = process.env.REACT_APP_BACKEND_URL;

export const getTenantId = () => {
  let tenantDb = localStorage.getItem('tenant_db');
  if (!tenantDb) {
    try {
      const list = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (list.length > 0 && list[0].database_name) tenantDb = list[0].database_name;
    } catch (e) { /* noop */ }
  }
  if (!tenantDb) {
    try {
      const tu = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tu.tenants && tu.tenants[0]) tenantDb = tu.tenants[0].database_name;
    } catch (e) { /* noop */ }
  }
  return (tenantDb || '').replace(/^tenant_/, '');
};

export const CartaViniQRModal = ({ open, onClose, getAuthHeaders }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selezione, setSelezione] = useState(null);
  const tenantId = getTenantId();
  const urlCarta = `${window.location.origin}/carta-vini/${tenantId}`;

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/carta-vini/settings`, { headers: getAuthHeaders() })
      .then(r => setSettings(r.data))
      .catch(() => setSettings({}));
    axios.get(`${API}/api/carta-vini/selezione`, { headers: getAuthHeaders() })
      .then(r => setSelezione(r.data))
      .catch(() => setSelezione(null));
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const mostraTutti = async () => {
    try {
      await axios.post(`${API}/api/carta-vini/selezione`, { vini_ids: [] }, { headers: getAuthHeaders() });
      setSelezione({ attiva: false, vini_ids: [], nome: '', count: 0 });
      toast.success('La carta pubblica ora mostra tutti i vini');
    } catch (e) {
      toast.error('Errore aggiornamento selezione');
    }
  };

  const caricaLogo = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const max = 320;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        let dataUrl = canvas.toDataURL('image/png');
        if (dataUrl.length > 300000) {
          const c2 = document.createElement('canvas');
          c2.width = canvas.width; c2.height = canvas.height;
          const ctx2 = c2.getContext('2d');
          ctx2.fillStyle = '#ffffff';
          ctx2.fillRect(0, 0, c2.width, c2.height);
          ctx2.drawImage(canvas, 0, 0);
          dataUrl = c2.toDataURL('image/jpeg', 0.85);
        }
        if (dataUrl.length > 600000) {
          toast.error('Immagine troppo complessa: usa un logo più semplice o più piccolo');
          return;
        }
        setSettings(prev => ({ ...prev, logo_url: dataUrl }));
        toast.success('Logo caricato: ricorda di salvare le impostazioni');
      };
      img.onerror = () => toast.error('Formato immagine non supportato: usa PNG o JPG');
      img.src = ev.target.result;
    };
    reader.onerror = () => toast.error('Impossibile leggere il file');
    reader.readAsDataURL(file);
  };

  const salva = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/carta-vini/settings`, settings, { headers: getAuthHeaders() });
      toast.success('Impostazioni carta salvate');
    } catch (e) {
      toast.error('Errore salvataggio');
    }
    setSaving(false);
  };

  const scaricaQR = () => {
    const canvas = document.querySelector('#qr-carta-vini canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'qr-carta-vini.png';
    a.click();
  };

  const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="qr-carta-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-lg">🍷 Carta dei Vini Digitale (QR)</h3>
          <button onClick={onClose} data-testid="qr-modal-close" className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* QR + link */}
          <div className="flex flex-col items-center gap-3" id="qr-carta-vini">
            <QRCodeCanvas value={urlCarta} size={180} level="M" includeMargin />
            <div className="flex items-center gap-2 w-full">
              <input readOnly value={urlCarta} data-testid="qr-link-input" className="flex-1 text-xs border rounded-lg px-2 py-2 bg-gray-50 text-gray-600" />
              <button onClick={() => { navigator.clipboard.writeText(urlCarta); toast.success('Link copiato'); }} data-testid="qr-copia-link" className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Copia link"><Copy className="h-4 w-4" /></button>
              <button onClick={scaricaQR} data-testid="qr-scarica" className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Scarica QR"><Download className="h-4 w-4" /></button>
              <a href={urlCarta} target="_blank" rel="noreferrer" data-testid="qr-apri-carta" className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200" title="Apri carta"><ExternalLink className="h-4 w-4" /></a>
            </div>
            <p className="text-xs text-gray-500 text-center">Stampa il QR e mettilo sui tavoli: i clienti leggono la carta dal telefono in 6 lingue.</p>
          </div>

          {/* Stato selezione pubblicata */}
          {selezione && (
            <div data-testid="stato-selezione-pubblicata" className={`rounded-xl p-3 text-sm flex items-center justify-between gap-2 ${selezione.attiva ? 'bg-amber-50 border border-amber-300' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-2">
                <Users className={`h-4 w-4 ${selezione.attiva ? 'text-amber-600' : 'text-green-600'}`} />
                <span className={selezione.attiva ? 'text-amber-800' : 'text-green-800'}>
                  {selezione.attiva
                    ? `Selezione attiva: ${selezione.count} vini pubblicati`
                    : 'Carta pubblica: tutti i vini'}
                </span>
              </div>
              {selezione.attiva && (
                <button onClick={mostraTutti} data-testid="mostra-tutti-vini-btn"
                  className="text-xs px-2.5 py-1.5 bg-amber-600 text-white rounded-lg whitespace-nowrap">
                  Mostra tutti
                </button>
              )}
            </div>
          )}

          {/* Personalizzazione layout */}
          {settings && (
            <div className="space-y-3 border-t pt-4">
              <h4 className="font-semibold text-sm">Personalizza layout</h4>
              <input placeholder="Nome ristorante" value={settings.nome_ristorante || ''} onChange={e => set('nome_ristorante', e.target.value)}
                data-testid="carta-nome-ristorante-input" className="w-full border rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Sottotitolo (es. Carta dei Vini)" value={settings.sottotitolo || ''} onChange={e => set('sottotitolo', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
              {/* Logo ristorante */}
              <div>
                <label className="text-xs text-gray-600 font-medium">Logo del ristorante</label>
                <div className="flex items-center gap-3 mt-1">
                  {settings.logo_url ? (
                    <div className="relative">
                      <img src={settings.logo_url} alt="Logo" data-testid="logo-preview"
                        className="h-16 w-24 object-contain border rounded-lg bg-gray-50" />
                      <button onClick={() => set('logo_url', '')} data-testid="logo-rimuovi"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1" title="Rimuovi logo">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label data-testid="logo-upload-btn"
                      className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-purple-400 hover:text-purple-600">
                      <ImageIcon className="h-4 w-4" />
                      Carica logo (PNG/JPG)
                      <input type="file" accept="image/*" className="hidden"
                        onChange={e => caricaLogo(e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Tema</label>
                <div className="flex gap-2 mt-1">
                  {[['avorio', 'Avorio', '#faf6ef'], ['scuro', 'Elegante Scuro', '#151210'], ['bordeaux', 'Bordeaux', '#2b1218']].map(([val, label, col]) => (
                    <button key={val} onClick={() => set('tema', val)} data-testid={`tema-${val}`}
                      className={`flex-1 py-2 px-2 rounded-lg text-xs border-2 ${settings.tema === val ? 'border-purple-600 font-semibold' : 'border-gray-200'}`}>
                      <span className="inline-block w-3 h-3 rounded-full mr-1 align-middle border" style={{ background: col }} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[['mostra_prezzi', 'Mostra prezzi'], ['mostra_descrizioni', 'Mostra descrizioni'], ['mostra_immagini', 'Mostra immagini'], ['mostra_riconoscimenti', 'Mostra premi']].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={settings[k] !== false} onChange={e => set(k, e.target.checked)} className="rounded" />
                    {label}
                  </label>
                ))}
              </div>
              <FotoBottiglieSection getAuthHeaders={getAuthHeaders} />
              <button onClick={salva} disabled={saving} data-testid="carta-salva-settings"
                className="w-full py-2.5 bg-purple-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Salvataggio...' : 'Salva impostazioni'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
